import { useCallback, useMemo } from 'react';
import type { NoteCategory } from '@bliks-io/contracts';
import {
  useAddDraftNote,
  useUpdateDraftNote,
  usePlaceDraftNote,
  useDeleteDraftNote
} from '@/api/mutations/useProcessDraft';

const DEFAULT_NOTE_SIZE = { width: 220, height: 120 };
const DEFAULT_CATEGORY: NoteCategory = 'context';

export interface NoteGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NoteActions {
  /** Create a note at a flow position; optionally pinned to target node(s). */
  addNote: (position: { x: number; y: number }, targets?: string[]) => Promise<void>;
  /** Edit a note's text and/or category. */
  updateNote: (noteId: string, patch: { text?: string; category?: NoteCategory }) => Promise<void>;
  /** Persist a note's geometry after a move or resize. */
  placeNote: (noteId: string, geometry: NoteGeometry) => Promise<void>;
  /** Replace a note's target node set (re-pin / unpin). */
  retargetNote: (noteId: string, targets: string[]) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
}

interface UseNoteActionsArgs {
  processId: string;
}

/**
 * Forward-only note actions (ADR 0008). Each call performs its draft mutation;
 * the backend's `@CaptureDraftHistory()` records the undo/redo entry — notes are
 * part of the draft-history snapshot (ADR 0007) — and the toolbar's undo/redo
 * steps the timeline. No client-side inverse bookkeeping.
 */
export function useNoteActions({ processId }: UseNoteActionsArgs): NoteActions {
  const addMutation = useAddDraftNote(processId);
  const updateMutation = useUpdateDraftNote(processId);
  const placeMutation = usePlaceDraftNote(processId);
  const deleteMutation = useDeleteDraftNote(processId);

  const addAsync = addMutation.mutateAsync;
  const updateAsync = updateMutation.mutateAsync;
  const placeAsync = placeMutation.mutateAsync;
  const deleteAsync = deleteMutation.mutateAsync;

  const addNote = useCallback(
    async (position: { x: number; y: number }, targets: string[] = []) => {
      await addAsync({
        text: '',
        category: DEFAULT_CATEGORY,
        geometry: { x: position.x, y: position.y, ...DEFAULT_NOTE_SIZE },
        targets
      });
    },
    [addAsync]
  );

  const updateNote = useCallback(
    async (noteId: string, patch: { text?: string; category?: NoteCategory }) => {
      await updateAsync({ noteId, patch });
    },
    [updateAsync]
  );

  const placeNote = useCallback(
    async (noteId: string, geometry: NoteGeometry) => {
      await placeAsync({ noteId, patch: { geometry } });
    },
    [placeAsync]
  );

  const retargetNote = useCallback(
    async (noteId: string, targets: string[]) => {
      await placeAsync({ noteId, patch: { targets } });
    },
    [placeAsync]
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      await deleteAsync(noteId);
    },
    [deleteAsync]
  );

  return useMemo(
    () => ({ addNote, updateNote, placeNote, retargetNote, deleteNote }),
    [addNote, updateNote, placeNote, retargetNote, deleteNote]
  );
}
